import React, { useState } from "react";
import { Box, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    Ban,
    MoreVertical,
    PlayCircle,
    CheckCircle2,
    Circle,
    Settings,
    Trash2,
    EyeOff,
    Eye,
} from "lucide-react";

import { Card, CardContent } from "./Card";
import { Badge } from "./Badge";
import { ReadOnlyBadge } from "./ReadOnlyBadge";
import { Progress } from "./Progress";
import { Button } from "./Button";
import {
    DropdownMenuContent,
    DropdownMenuItem,
} from "./DropdownMenu";

const projectStateColorMap = {
    completed: "green",
    in_progress: "yellow",
    not_started: "blue",
    cancelled: "red",
    no_necesario: "gray",
};

const projectStateLabels = {
    completed: "Completado",
    in_progress: "En Progreso",
    not_started: "No Iniciado",
    cancelled: "Cancelado",
    no_necesario: "No Necesario",
};

/**
 * Componente de tarjeta de proyecto reutilizable
 * 
 * @param {Object} props
 * @param {Object} props.project - Objeto del proyecto con state, progress_percentage, etc.
 * @param {Function} props.onClick - Callback al hacer click en la tarjeta (project, event)
 * @param {Function} [props.onStateChange] - Callback para cambiar el estado (projectId, newState)
 * @param {string} props.title - Título a mostrar en la tarjeta
 * @param {Array} [props.infoItems] - Array de items de info [{icon: Component, text: string, color?: string, textColor?: string, fontWeight?: number}]
 * @param {Array} [props.secondaryInfoItems] - Array de items que van en una segunda línea
 * @param {boolean} [props.showActionButton=false] - Mostrar botón de acción
 * @param {string} [props.actionButtonLabel="Progreso"] - Label del botón de acción
 * @param {React.ReactNode} [props.actionButtonIcon] - Icono del botón de acción
 * @param {Function} [props.onActionClick] - Callback del botón de acción (project)
 * @param {Function} [props.onDelete] - Callback para eliminar el proyecto (projectId)
 * @param {Function} [props.onToggleVisibility] - Callback para ocultar/mostrar el proyecto (projectId, currentIsInternal)
 * @param {boolean} [props.canToggleVisibility=false] - Si se puede ocultar/mostrar el proyecto
 * @param {Function} [props.onConfigureRecurrence] - Callback para abrir la configuración de recurrencia (project)
 * @param {boolean} [props.forceMenuOpen=false] - Mantiene el menú de acciones abierto (uso tutorial)
 * @param {string} [props.menuButtonDataTutorial] - data-tutorial para el botón del menú
 * @param {string} [props.recurrenceMenuItemDataTutorial] - data-tutorial para la acción de recurrencia
 * @param {boolean} [props.highlightRecurrenceMenuItem=false] - Resalta la acción de recurrencia
 */
function ProjectCardBase({
    project,
    onClick,
    onStateChange,
    title,
    infoItems = [],
    secondaryInfoItems = [],
    showActionButton = false,
    actionButtonLabel = "Progreso",
    actionButtonIcon,
    onActionClick,
    onDelete,
    onToggleVisibility,
    canToggleVisibility = false,
    onConfigureRecurrence,
    forceMenuOpen = false,
    menuButtonDataTutorial,
    recurrenceMenuItemDataTutorial,
    highlightRecurrenceMenuItem = false,
    allowNotNeeded = false,
    readOnly = false,
}) {
    const theme = useTheme();
    const badgeColor = projectStateColorMap[project.state] || "gray";
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuButtonEl, setMenuButtonEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const resolvedMenuAnchorEl = forceMenuOpen ? menuButtonEl : menuAnchorEl;
    const resolvedMenuOpen = forceMenuOpen ? Boolean(menuButtonEl) : menuOpen;

    const progress = Math.round(project.progress_percentage || 0);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleStateChange = (newState) => {
        handleMenuClose();
        if (onStateChange) {
            // Pass previousState (current project.state) as third parameter
            onStateChange(project.id, newState, project.state);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        handleMenuClose();
        if (onDelete) {
            onDelete(project.id);
        }
    };

    const handleConfigureRecurrence = (e) => {
        e.stopPropagation();
        handleMenuClose();
        if (onConfigureRecurrence) {
            onConfigureRecurrence(project);
        }
    };

    return (
        <Card
            onClick={(event) => {
                if (onClick) {
                    onClick(project, event);
                }
            }}
            sx={{
                height: "100%",
                width: "100%",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                    boxShadow:
                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                },
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                    {/* Header con título, badge y menú */}
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                            <Typography
                                component="h3"
                                sx={{
                                    fontSize: "1.05rem",
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {title}
                            </Typography>
                            <Badge color={badgeColor} size="sm" sx={{ flexShrink: 0 }}>
                                {projectStateLabels[project.state] || project.state}
                            </Badge>
                            {readOnly && <ReadOnlyBadge />}
                        </Box>
                        {(onStateChange || onDelete || canToggleVisibility || onConfigureRecurrence) && (
                            <>
                                <Tooltip title="Ver acciones" arrow >
                                    <IconButton
                                        ref={setMenuButtonEl}
                                        data-tutorial={menuButtonDataTutorial}
                                        size="small"
                                        onClick={handleMenuOpen}
                                        sx={{
                                            width: "28px",
                                            height: "28px",
                                            padding: 0,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MoreVertical size={16} />
                                    </IconButton>
                                </Tooltip>
                                <DropdownMenuContent
                                    anchorEl={resolvedMenuAnchorEl}
                                    open={resolvedMenuOpen}
                                    onClose={forceMenuOpen ? () => undefined : handleMenuClose}
                                    align="end"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {onStateChange && project.state !== "in_progress" && (
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStateChange("in_progress");
                                            }}
                                        >
                                            <PlayCircle size={16} />
                                            En progreso
                                        </DropdownMenuItem>
                                    )}
                                    {onStateChange && project.state !== "completed" && (
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStateChange("completed");
                                            }}
                                        >
                                            <CheckCircle2 size={16} />
                                            Completado
                                        </DropdownMenuItem>
                                    )}
                                    {onStateChange && project.state !== "not_started" && (
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStateChange("not_started");
                                            }}
                                        >
                                            <Circle size={16} />
                                            No iniciado
                                        </DropdownMenuItem>
                                    )}
                                    {onStateChange && allowNotNeeded && project.state !== "no_necesario" && (
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStateChange("no_necesario");
                                            }}
                                        >
                                            <Ban size={16} />
                                            No necesario
                                        </DropdownMenuItem>
                                    )}
                                    {canToggleVisibility && onToggleVisibility && (
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMenuClose();
                                                onToggleVisibility(project.id, project.is_internal);
                                            }}
                                        >
                                            {project.is_internal ? (
                                                <>
                                                    <Eye size={16} />
                                                    Mostrar al cliente
                                                </>
                                            ) : (
                                                <>
                                                    <EyeOff size={16} />
                                                    Ocultar al cliente
                                                </>
                                            )}
                                        </DropdownMenuItem>
                                    )}
                                    {onConfigureRecurrence && (
                                        <DropdownMenuItem
                                            data-tutorial={recurrenceMenuItemDataTutorial}
                                            onClick={handleConfigureRecurrence}
                                            sx={
                                                highlightRecurrenceMenuItem
                                                    ? {
                                                        border: `2px solid ${theme.palette.error.main}`,
                                                        borderRadius: "8px",
                                                        "&:hover": {
                                                            backgroundColor: theme.palette.action.hover,
                                                        },
                                                    }
                                                    : undefined
                                            }
                                        >
                                            <Settings size={16} />
                                            Configurar recurrencia
                                        </DropdownMenuItem>
                                    )}
                                    {onDelete && (
                                        <DropdownMenuItem
                                            onClick={handleDelete}
                                            sx={{ color: "error.main" }}
                                        >
                                            <Trash2 size={16} />
                                            Eliminar proyecto
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </>
                        )}
                    </Stack>

                    {/* Info items */}
                    {infoItems.length > 0 && (
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            {infoItems.map((item, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && item.separator && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: theme.palette.text.secondary }}
                                        >
                                            •
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        {item.icon && (
                                            <item.icon
                                                size={12}
                                                color={item.color || theme.palette.primary.main}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: item.textColor || theme.palette.text.secondary,
                                                fontWeight: item.fontWeight || 400,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.text}
                                        </Typography>
                                    </Stack>
                                </React.Fragment>
                            ))}
                        </Stack>
                    )}

                    {/* Secondary info items (nueva línea) */}
                    {secondaryInfoItems.length > 0 && (
                        <Stack direction="row" spacing={2} alignItems="center">
                            {secondaryInfoItems.map((item, index) => {
                                const TrailingIcon = item.trailingIcon;
                                return (
                                    <Stack key={index} direction="row" spacing={0.5} alignItems="center">
                                        {item.icon && (
                                            <item.icon
                                                size={12}
                                                color={item.color || theme.palette.primary.main}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: item.textColor || theme.palette.text.secondary,
                                                fontWeight: item.fontWeight || 400,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.text}
                                        </Typography>
                                        {TrailingIcon && (
                                            <TrailingIcon
                                                size={14}
                                                color={theme.palette.text.secondary}
                                            />
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}

                    {/* Barra de progreso */}
                    <Box>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 0.5 }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                Progreso general
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                {progress}%
                            </Typography>
                        </Stack>
                        <Progress value={project.progress_percentage || 0} />
                    </Box>

                    {/* Botón de acción opcional */}
                    {showActionButton && (
                        <Stack direction="row" justifyContent="flex-end">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (onActionClick) {
                                        onActionClick(project);
                                    }
                                }}
                            >
                                {actionButtonIcon}
                                {actionButtonLabel}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

const normalizeProgress = (value) => (value == null ? 0 : value);

const areEqualProjectCardProps = (prev, next) => {
    const prevProject = prev.project;
    const nextProject = next.project;

    if (prevProject?.id !== nextProject?.id) return false;
    if (prevProject?.state !== nextProject?.state) return false;
    if (
        normalizeProgress(prevProject?.progress_percentage) !==
        normalizeProgress(nextProject?.progress_percentage)
    ) {
        return false;
    }
    if (prevProject?.client_name !== nextProject?.client_name) return false;
    if (prevProject?.name !== nextProject?.name) return false;
    if (prevProject?.template_name !== nextProject?.template_name) return false;
    if (
        prevProject?.setup_variant_display !== nextProject?.setup_variant_display
    ) {
        return false;
    }
    if (prevProject?.start_date !== nextProject?.start_date) return false;
    if (prevProject?.is_internal !== nextProject?.is_internal) return false;

    if (prev.title !== next.title) return false;
    if (prev.showActionButton !== next.showActionButton) return false;
    if (prev.actionButtonLabel !== next.actionButtonLabel) return false;
    if (prev.actionButtonIcon !== next.actionButtonIcon) return false;
    if (prev.onClick !== next.onClick) return false;
    if (prev.onActionClick !== next.onActionClick) return false;
    if (prev.onStateChange !== next.onStateChange) return false;
    if (prev.onDelete !== next.onDelete) return false;
    if (prev.onToggleVisibility !== next.onToggleVisibility) return false;
    if (prev.canToggleVisibility !== next.canToggleVisibility) return false;
    if (prev.onConfigureRecurrence !== next.onConfigureRecurrence) return false;
    if (prev.forceMenuOpen !== next.forceMenuOpen) return false;
    if (prev.readOnly !== next.readOnly) return false;
    if (prev.menuButtonDataTutorial !== next.menuButtonDataTutorial) return false;
    if (
        prev.recurrenceMenuItemDataTutorial !== next.recurrenceMenuItemDataTutorial
    ) {
        return false;
    }
    if (
        prev.highlightRecurrenceMenuItem !== next.highlightRecurrenceMenuItem
    ) {
        return false;
    }

    return true;
};

export const ProjectCard = React.memo(
    ProjectCardBase,
    areEqualProjectCardProps
);
